import { useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DataTable } from "@/components/admin/data-table";
import { useGpsAlertEvents, GpsAlertEvent } from "@/hooks/useGpsAlertEvents";
import { useAcknowledgeGpsAlert } from "@/hooks/useAcknowledgeGpsAlert";
import {
  Zap,
  MapPin,
  Battery,
  Timer,
  AlertTriangle,
  MoreHorizontal,
  Eye,
  CheckCircle,
  Clock,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface GpsAlertsTableProps {
  companyId: string;
}

const getAlertTypeIcon = (type: string) => {
  switch (type) {
    case 'overspeed':
      return <Zap className="h-4 w-4 text-red-600" />;
    case 'enter':
    case 'exit':
      return <MapPin className="h-4 w-4 text-blue-600" />;
    case 'battery_low':
      return <Battery className="h-4 w-4 text-yellow-600" />;
    case 'idle':
      return <Timer className="h-4 w-4 text-orange-600" />;
    default:
      return <AlertTriangle className="h-4 w-4" />;
  }
};

const getPriorityVariant = (priority: string): "destructive" | "secondary" | "default" | "outline" => {
  switch (priority) {
    case 'high':
      return 'destructive';
    case 'medium':
      return 'secondary';
    case 'low':
      return 'default';
    default:
      return 'outline';
  }
};

const getPriorityLabel = (priority: string) => {
  switch (priority) {
    case 'high':
      return 'Alta';
    case 'medium':
      return 'Média';
    case 'low':
      return 'Baixa';
    default:
      return 'Normal';
  }
};

const getAlertTypeLabel = (type: string) => {
  switch (type) {
    case 'overspeed':
      return 'Excesso de Velocidade';
    case 'enter':
      return 'Entrada em Geocerca';
    case 'exit':
      return 'Saída de Geocerca';
    case 'battery_low':
      return 'Bateria Baixa';
    case 'idle':
      return 'Ociosidade';
    case 'power_loss':
      return 'Perda de Energia';
    default:
      return type;
  }
};

export function GpsAlertsTable({ companyId }: GpsAlertsTableProps) {
  const { data: alerts = [], isLoading } = useGpsAlertEvents(companyId);
  const acknowledgeMutation = useAcknowledgeGpsAlert(companyId);
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'acknowledged'>('all');

  const filteredAlerts = alerts.filter((alert: any) => {
    if (statusFilter === 'pending') return !alert.ack_at;
    if (statusFilter === 'acknowledged') return !!alert.ack_at;
    return true;
  });

  const columns: ColumnDef<GpsAlertEvent>[] = [
    {
      accessorKey: 'alert_type',
      header: 'Alerta',
      cell: ({ row }) => {
        const type = row.original.alert_type;
        const name = row.original.alert_rules?.name || getAlertTypeLabel(type);
        return (
          <div className="flex items-center gap-2">
            {getAlertTypeIcon(type)}
            <span className="font-medium">{name}</span>
          </div>
        );
      },
    },
    {
      header: 'Dispositivo/Veículo',
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.original.devices?.name || 'N/A'}</div>
          {row.original.fleet_vehicles && (
            <div className="text-sm text-muted-foreground">
              {row.original.fleet_vehicles.plate}
              {row.original.fleet_vehicles.make && ` • ${row.original.fleet_vehicles.make}`}
            </div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'priority',
      header: 'Prioridade',
      cell: ({ row }) => {
        const priority = row.getValue('priority') as string;
        return (
          <Badge variant={getPriorityVariant(priority)}>
            {getPriorityLabel(priority)}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'happened_at',
      header: 'Data/Hora',
      cell: ({ row }) => {
        const date = new Date(row.getValue('happened_at'));
        return (
          <div className="text-sm">
            <div>{date.toLocaleDateString('pt-BR')}</div>
            <div className="text-muted-foreground">
              {date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        );
      },
    },
    {
      header: 'Status',
      cell: ({ row }) => {
        const alert = row.original;
        return alert.ack_at ? (
          <Badge variant="outline" className="border-green-600 text-green-600">
            <CheckCircle className="h-3 w-3 mr-1" />
            Reconhecido
          </Badge>
        ) : (
          <Badge variant="outline" className="border-orange-600 text-orange-600">
            <Clock className="h-3 w-3 mr-1" />
            Pendente
          </Badge>
        );
      },
    },
    {
      id: 'actions',
      cell: ({ row }) => {
        const alert = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem>
                <Eye className="mr-2 h-4 w-4" />
                Ver Detalhes
              </DropdownMenuItem>
              {!alert.ack_at && (
                <DropdownMenuItem 
                  onClick={() => acknowledgeMutation.mutate(alert.id)}
                  disabled={acknowledgeMutation.isPending}
                >
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Reconhecer
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Tabs value={statusFilter} onValueChange={(v) => setStatusFilter(v as any)}>
          <TabsList>
            <TabsTrigger value="all">Todos ({alerts.length})</TabsTrigger>
            <TabsTrigger value="pending">
              Pendentes ({alerts.filter((a: any) => !a.ack_at).length})
            </TabsTrigger>
            <TabsTrigger value="acknowledged">
              Reconhecidos ({alerts.filter((a: any) => !!a.ack_at).length})
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <DataTable
        columns={columns}
        data={filteredAlerts as any}
        searchKey="devices.name"
        searchPlaceholder="Buscar por dispositivo..."
      />
    </div>
  );
}
